import React, { useEffect, useState } from 'react';
import {
  AlertCircle, BellRing, Camera, CheckCircle2, Cloud, Mail, Moon, RefreshCw, Save, Settings, Sun, Trash2, UserRound,
} from 'lucide-react';
import { useAuth, usePeople, useTheme, useZoho } from '../../context';
import { apiDeleteProfilePicture, apiGetR2Storage, apiUpdateMyProfile, apiUploadProfilePicture, apiZohoAuthUrl } from '../../api';
import {
  disablePushNotifications,
  enablePushNotifications,
  getPushNotificationState,
  syncPushSubscription,
} from '../../pushNotifications';
import ModalShell from '../Modal/ModalShell';
import { PersonAvatar } from '../People/PeopleUI';

const prepareProfileImage = (file) => new Promise((resolve, reject) => {
  if (!file?.type?.startsWith('image/')) { reject(new Error('Choose an image file')); return; }
  const image = new Image();
  const url = URL.createObjectURL(file);
  image.onload = () => {
    const max = 360;
    const scale = Math.min(1, max / Math.max(image.naturalWidth, image.naturalHeight));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.naturalWidth * scale));
    canvas.height = Math.max(1, Math.round(image.naturalHeight * scale));
    canvas.getContext('2d').drawImage(image, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    canvas.toBlob((blob) => {
      if (!blob) { reject(new Error('Could not prepare this image')); return; }
      resolve(new File([blob], 'profile.webp', { type: 'image/webp' }));
    }, 'image/webp', 0.84);
  };
  image.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Could not read this image')); };
  image.src = url;
});

const formatBytes = (bytes) => {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let amount = value;
  let index = -1;
  do { amount /= 1024; index += 1; } while (amount >= 1024 && index < units.length - 1);
  return `${amount >= 10 ? amount.toFixed(1) : amount.toFixed(2)} ${units[index]}`;
};

export default function SettingsMenu({ open: controlledOpen, onOpenChange, showTrigger = true }) {
  const { user, jwtToken, updateSessionUser } = useAuth();
  const { refreshPeople } = usePeople();
  const { connected: zohoConnected } = useZoho() || {};
  const { theme, toggleTheme } = useTheme();
  const [internalOpen, setInternalOpen] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageLoading, setStorageLoading] = useState(false);
  const [storageError, setStorageError] = useState('');
  const [connectingZoho, setConnectingZoho] = useState(false);
  const [pushStatus, setPushStatus] = useState('checking');
  const [pushError, setPushError] = useState('');
  const [profileName, setProfileName] = useState(user?.name || '');
  const [profileDescription, setProfileDescription] = useState(user?.description || '');
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileUploading, setProfileUploading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSaved, setProfileSaved] = useState(false);
  const isControlled = typeof controlledOpen === 'boolean';
  const open = isControlled ? controlledOpen : internalOpen;
  const isAdmin = user?.role === 'admin';
  const isClient = user?.role === 'client';

  const setOpen = (value) => {
    if (!isControlled) setInternalOpen(value);
    onOpenChange?.(value);
  };

  const loadStorage = async () => {
    if (!isAdmin || !jwtToken) return;
    setStorageLoading(true);
    setStorageError('');
    try {
      setStorage(await apiGetR2Storage(jwtToken));
    } catch (error) {
      setStorageError(error.message || 'Could not load storage usage');
    } finally {
      setStorageLoading(false);
    }
  };

  useEffect(() => {
    if (open && isAdmin && !storage && !storageLoading) loadStorage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isAdmin]);

  useEffect(() => {
    if (!open) return;
    setProfileName(user?.name || '');
    setProfileDescription(user?.description || '');
    setProfileError('');
    setProfileSaved(false);
  }, [open, user?.name, user?.description]);

  const saveProfile = async (event) => {
    event.preventDefault();
    if (!profileName.trim()) { setProfileError('Your name is required'); return; }
    setProfileSaving(true); setProfileError(''); setProfileSaved(false);
    try {
      const updated = await apiUpdateMyProfile(jwtToken, { name: profileName.trim(), ...(!isClient ? { description: profileDescription.trim() } : {}) });
      updateSessionUser(updated);
      await refreshPeople().catch(() => {});
      setProfileSaved(true);
    } catch (error) { setProfileError(error.message || 'Could not save your profile'); }
    finally { setProfileSaving(false); }
  };

  const changePicture = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setProfileUploading(true); setProfileError(''); setProfileSaved(false);
    try {
      const prepared = await prepareProfileImage(file);
      const updated = await apiUploadProfilePicture(jwtToken, prepared);
      updateSessionUser(updated);
      await refreshPeople().catch(() => {});
      setProfileSaved(true);
    } catch (error) { setProfileError(error.message || 'Could not update your profile picture'); }
    finally { setProfileUploading(false); }
  };

  const removePicture = async () => {
    setProfileUploading(true); setProfileError(''); setProfileSaved(false);
    try {
      const updated = await apiDeleteProfilePicture(jwtToken);
      updateSessionUser(updated);
      await refreshPeople().catch(() => {});
      setProfileSaved(true);
    } catch (error) { setProfileError(error.message || 'Could not remove your profile picture'); }
    finally { setProfileUploading(false); }
  };

  useEffect(() => {
    let active = true;
    if (!jwtToken) {
      setPushStatus('disabled');
      return undefined;
    }
    syncPushSubscription(jwtToken)
      .then((status) => { if (active) setPushStatus(status); })
      .catch(async () => {
        const status = await getPushNotificationState().catch(() => 'disabled');
        if (active) setPushStatus(status);
      });
    return () => { active = false; };
  }, [jwtToken]);

  const reconnectZoho = async () => {
    if (connectingZoho) return;
    setConnectingZoho(true);
    try {
      const { url } = await apiZohoAuthUrl(jwtToken);
      if (url) window.location.href = url;
    } catch (error) {
      setConnectingZoho(false);
      alert(`Failed to start Zoho authorization: ${error.message}`);
    }
  };

  const togglePush = async () => {
    if (['checking', 'saving', 'unsupported', 'install_required', 'insecure'].includes(pushStatus)) return;
    setPushStatus('saving');
    setPushError('');
    try {
      const status = pushStatus === 'enabled'
        ? await disablePushNotifications(jwtToken)
        : await enablePushNotifications(jwtToken);
      setPushStatus(status);
    } catch (error) {
      const status = await getPushNotificationState().catch(() => 'disabled');
      setPushStatus(status);
      setPushError(error.message || 'Could not update browser notifications');
    }
  };

  const pushDescription = pushError || ({
    checking: 'Checking this device…',
    saving: 'Updating this device…',
    enabled: 'Alerts can arrive while the portal is closed',
    blocked: 'Blocked in your browser or phone settings',
    install_required: 'On iPhone, add the portal to your Home Screen first',
    insecure: 'Requires a secure HTTPS connection',
    unsupported: 'Not supported by this browser',
    disabled: 'Receive new activity on this device',
  }[pushStatus]);

  const percentage = storage?.limitBytes
    ? Math.min(100, Math.round((storage.usedBytes / storage.limitBytes) * 100))
    : null;

  return (
    <>
      {showTrigger && (
        <button type="button" className={`settings-trigger ${open ? 'active' : ''}`} onClick={() => setOpen(!open)} aria-label="Open settings" aria-expanded={open} title="Settings">
          <Settings size={18} />
        </button>
      )}

      {open && (
        <ModalShell
          title="Settings"
          subtitle="Manage your workspace preferences, notifications, and connected services."
          eyebrow="Workspace"
          icon={Settings}
          size="lg"
          className="settings-modal"
          bodyClassName="settings-modal-body"
          onClose={() => setOpen(false)}
        >
          <div className="settings-modal-grid">
            <section className="settings-section settings-profile-section">
              <span className="settings-section-label">Your profile</span>
              <form className="settings-profile-form" onSubmit={saveProfile}>
                <div className="settings-profile-photo">
                  <PersonAvatar person={user} size="xl" />
                  <label className="btn btn-ghost btn-sm"><Camera size={14} />{profileUploading ? 'Updating…' : 'Change photo'}<input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={changePicture} hidden disabled={profileUploading} /></label>
                  {user?.avatarData && <button type="button" className="settings-remove-photo" onClick={removePicture} disabled={profileUploading}><Trash2 size={13} />Remove</button>}
                </div>
                <div className="settings-profile-fields">
                  <label><span>Name</span><div className="settings-profile-input"><UserRound size={15} /><input value={profileName} onChange={(event) => { setProfileName(event.target.value); setProfileSaved(false); }} maxLength={100} required /></div></label>
                  {!isClient && <label><span>Description</span><textarea value={profileDescription} onChange={(event) => { setProfileDescription(event.target.value); setProfileSaved(false); }} maxLength={280} rows={3} placeholder="Tell people what you work on or how you can help." /><small>{profileDescription.length}/280</small></label>}
                  <div className="settings-profile-footer">{profileError ? <span className="settings-profile-error"><AlertCircle size={14} />{profileError}</span> : profileSaved ? <span className="settings-profile-success"><CheckCircle2 size={14} />Profile updated</span> : <span />}
                    <button type="submit" className="btn btn-primary btn-sm" disabled={profileSaving || profileUploading}><Save size={14} />{profileSaving ? 'Saving…' : 'Save profile'}</button>
                  </div>
                </div>
              </form>
            </section>

            <section className="settings-section">
              <span className="settings-section-label">Appearance</span>
              <button type="button" className="settings-row settings-theme-row" onClick={toggleTheme}>
                <span className="settings-row-icon">{theme === 'dark' ? <Moon size={18} /> : <Sun size={18} />}</span>
                <span className="settings-row-copy"><strong>Dark mode</strong><small>Use a darker interface throughout the portal</small></span>
                <span className={`settings-switch ${theme === 'dark' ? 'on' : ''}`} aria-hidden="true"><span /></span>
              </button>
            </section>

            <section className="settings-section">
              <span className="settings-section-label">Notifications</span>
              <button
                type="button"
                className="settings-row settings-theme-row"
                onClick={togglePush}
                disabled={['checking', 'saving', 'unsupported', 'install_required', 'insecure'].includes(pushStatus)}
              >
                <span className="settings-row-icon"><BellRing size={18} /></span>
                <span className="settings-row-copy">
                  <strong>Browser notifications</strong>
                  <small className={pushError || pushStatus === 'blocked' ? 'settings-warning' : pushStatus === 'enabled' ? 'settings-success' : ''}>{pushDescription}</small>
                </span>
                <span className={`settings-switch ${pushStatus === 'enabled' ? 'on' : ''}`} aria-hidden="true"><span /></span>
              </button>
            </section>

            {isAdmin && (
              <>
                <section className="settings-section">
                  <span className="settings-section-label">Email connection</span>
                  <div className="settings-service-row">
                    <span className="settings-row-icon"><Mail size={18} /></span>
                    <span className="settings-row-copy">
                      <strong>Zoho Mail</strong>
                      <small className={zohoConnected ? 'settings-success' : 'settings-warning'}>
                        {zohoConnected ? <CheckCircle2 size={12} /> : <AlertCircle size={12} />}
                        {zohoConnected ? 'Connected' : 'Needs connection'}
                      </small>
                    </span>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={reconnectZoho} disabled={connectingZoho}>
                      <RefreshCw size={13} className={connectingZoho ? 'spin-icon' : ''} /> Reconnect
                    </button>
                  </div>
                </section>

                <section className="settings-section">
                  <div className="settings-storage-heading">
                    <span className="settings-section-label">Cloudflare R2 storage</span>
                    <button type="button" onClick={loadStorage} disabled={storageLoading} aria-label="Refresh storage usage" title="Refresh storage usage">
                      <RefreshCw size={13} className={storageLoading ? 'spin-icon' : ''} />
                    </button>
                  </div>
                  <div className="settings-storage-card">
                    <span className="settings-row-icon"><Cloud size={18} /></span>
                    {storageLoading && !storage ? (
                      <span className="settings-storage-loading">Reading bucket usage…</span>
                    ) : storageError ? (
                      <span className="settings-storage-error">{storageError}</span>
                    ) : storage ? (
                      <div className="settings-storage-content">
                        <div className="settings-storage-values"><strong>{formatBytes(storage.usedBytes)} used</strong><span>{storage.limitBytes ? `${formatBytes(storage.remainingBytes)} left` : 'No fixed cap'}</span></div>
                        {percentage !== null && <div className="settings-storage-track"><span style={{ width: `${percentage}%` }} /></div>}
                        <small>{storage.objectCount.toLocaleString()} stored {storage.objectCount === 1 ? 'object' : 'objects'}</small>
                      </div>
                    ) : null}
                  </div>
                </section>
              </>
            )}
          </div>
        </ModalShell>
      )}
    </>
  );
}
